import { Banknote, Bus, FileText, Home, Layers, User, Users, type LucideIcon } from 'lucide-react';

import type { AdmissionFormValues } from './schemas/admission.schema';

/**
 * The admission form, described once.
 *
 * The panels down the page, the rail beside them and the "what still needs
 * checking" summary at the top are three readings of the same seven sections.
 * Written here so they cannot disagree about which field belongs where — a form
 * whose progress rail says the family section is done while the summary is
 * pointing at a bad mobile number in it is worse than no rail at all
 * (CLAUDE.md §3).
 *
 * `required` is the subset the office cannot save without, mirroring
 * `admission.schema.ts`. It drives the progress figure only; the schema is
 * still what decides whether a form may be submitted, and the server after it.
 */

export type AdmissionField = keyof AdmissionFormValues;

export interface AdmissionSectionSpec {
  key: string;
  /** "A. The child" — the same words the panel's own header carries. */
  label: string;
  /** What the section is for, one line, for the rail. */
  hint: string;
  icon: LucideIcon;
  /** Every field the panel holds, so an error can be traced back to a section. */
  fields: readonly AdmissionField[];
  /**
   * The ones that must be filled before the form will save.
   *
   * Empty for a section nothing is compulsory in — transport is offered, never
   * demanded, and a rail that marked it incomplete for ever would be telling
   * the office off for a choice it made. Documents are files, not fields, so
   * the form page counts them from `requiredDocuments` instead.
   */
  required: readonly AdmissionField[];
  /**
   * Replaces `required` where what is compulsory depends on another answer —
   * the family panel, where only the first contact must be filled in.
   */
  requiredFor?: (values: Partial<AdmissionFormValues>) => readonly AdmissionField[];
}

/** What the first contact must have: a name and a number. Mirrors the schema. */
const FIRST_CONTACT_FIELDS: Record<AdmissionFormValues['primaryContact'], readonly AdmissionField[]> = {
  FATHER: ['fatherName', 'fatherMobile'],
  MOTHER: ['motherName', 'motherMobile'],
  GUARDIAN: ['guardianName', 'guardianMobile'],
};

export const ADMISSION_SECTIONS: readonly AdmissionSectionSpec[] = [
  {
    key: 'child',
    label: 'The child',
    hint: 'Name, date of birth and identity',
    icon: User,
    fields: [
      'firstName',
      'middleName',
      'lastName',
      'email',
      'gender',
      'dateOfBirth',
      'bloodGroup',
      'religion',
      'category',
      'nationality',
      'aadhaarNumber',
    ],
    required: ['firstName', 'lastName', 'gender', 'dateOfBirth', 'nationality'],
  },
  {
    key: 'placement',
    label: 'Placement',
    hint: 'The class and section they join',
    icon: Layers,
    fields: [
      'classUuid',
      'sectionUuid',
      'admissionDate',
      'admissionType',
      'previousSchool',
      'previousClass',
      'transferCertificateNumber',
    ],
    required: ['classUuid', 'sectionUuid', 'admissionDate'],
  },
  {
    key: 'family',
    label: 'Family',
    hint: 'Parents, guardian and first contact',
    icon: Users,
    fields: [
      'fatherName',
      'fatherOccupation',
      'fatherMobile',
      'fatherEmail',
      'motherName',
      'motherOccupation',
      'motherMobile',
      'motherEmail',
      'guardianName',
      'guardianRelationship',
      'guardianMobile',
      'primaryContact',
    ],
    // No parent is compulsory in itself — only whoever the school rings first.
    required: ['primaryContact'],
    requiredFor: (values) => [
      'primaryContact',
      ...(values.primaryContact ? FIRST_CONTACT_FIELDS[values.primaryContact] : []),
    ],
  },
  {
    key: 'address',
    label: 'Address',
    hint: 'Where the child lives',
    icon: Home,
    fields: ['addressLine1', 'addressLine2', 'city', 'district', 'state', 'pincode'],
    required: ['addressLine1', 'city', 'state'],
  },
  {
    key: 'transport',
    label: 'Transport',
    hint: 'Only if they take the bus',
    icon: Bus,
    fields: ['usesTransport', 'routeName', 'busNumber', 'pickupPoint'],
    required: [],
  },
  {
    key: 'fees',
    label: 'Fees',
    hint: 'What is charged, and paid today',
    icon: Banknote,
    fields: [
      'tuitionFee',
      'tuitionDiscount',
      'tuitionPaid',
      'booksFee',
      'booksDiscount',
      'booksPaid',
      'otherFee',
      'otherDiscount',
      'otherPaid',
      'concessionReason',
      'feeDueDate',
    ],
    required: [],
  },
  {
    key: 'documents',
    label: 'Documents',
    hint: 'Certificates and proofs for this admission',
    icon: FileText,
    fields: [],
    required: [],
  },
] as const;

/** The dom id a section's panel carries, so the rail can scroll to it. */
export function sectionAnchorId(key: string): string {
  return `admission-section-${key}`;
}

/**
 * The air a section keeps above itself when the rail jumps to it.
 *
 * The console's top bar is `sticky top-0`, so a browser that scrolls an anchor
 * flush to the top of the viewport parks its heading *underneath* that bar —
 * the reader lands on a panel whose name they cannot see, which reads as having
 * jumped to the wrong place. `scroll-margin-top` is what the platform provides
 * for exactly this, and it has to clear the bar's own height (about 65px) with
 * a little left over.
 *
 * A constant rather than a class repeated on each of the seven panels, so the
 * day the bar changes height there is one number to move (CLAUDE.md §3).
 */
export const SECTION_SCROLL_MARGIN = 'scroll-mt-20';

/**
 * A value counts as filled when somebody has actually put something in it.
 *
 * `false` counts — an unticked "takes the bus" is an answer. Zero counts too,
 * because a fee of nothing is a decision the office made rather than a blank.
 */
export function isFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;

  return true;
}
